import Experience, { type Section as SectionType } from "@folio-2026/experience"
import { ScrollManager } from "@folio-2026/shared"
import assert from "assert"
import { SCROLL_TOLERANCE } from "const/index"
import Lenis from "lenis"
import { MathUtils } from "three"

import { Snap } from "./atoms"

export const handleScroll = (lenis: Lenis, snaps: Snap[], debug: boolean) => {
  const { isScrolling, isLocked } = lenis

  const experience = Experience.getInstance()
  // Use stable viewport height from experience.sizes instead of window.innerHeight
  // This prevents issues on mobile where window.innerHeight changes as browser UI bars hide/show
  const viewportHeight = experience.sizes?.height || window.innerHeight

  const scrollManager = ScrollManager.getInstance<SectionType>({
    viewportHeight,
    debug,
    experience,
  })

  const scrollSnap = scrollManager.getScrollTransition({
    scroll: MathUtils.clamp(
      lenis.animatedScroll,
      0,
      snaps[snaps.length - 1].snap + snaps[snaps.length - 1].height,
    ),
    snaps,
    scrollInfo: experience.scrollInfo,
  })

  const { currentSection, nextSection, direction } = scrollSnap || {}
  const progress = experience.scrollInfo.progress

  //
  // Update Experience.scrollInfo
  Object.assign(experience.scrollInfo, scrollSnap || {})

  if (
    isScrolling ||
    isLocked ||
    !snaps ||
    !currentSection ||
    typeof progress !== "number" ||
    !direction ||
    !nextSection
  ) {
    return
  }

  if (direction === -1 && progress < 1 - SCROLL_TOLERANCE) {
    assert(nextSection, "Next section not defined")

    //
    // Strong scroll up
    // Scroll to the bottom of the previous section then scroll to
    // the snap point (aka start of the section)
    // Use stable viewportHeight instead of window.innerHeight for mobile compatibility
    lenis.scrollTo(nextSection.snap + nextSection.height - viewportHeight)
  } else if (direction === -1 && progress >= 1 - SCROLL_TOLERANCE) {
    assert(nextSection, "Next section not defined")
    //
    // Weak scroll up
    // Adjust the current section scroll
    lenis.scrollTo(currentSection.snap)
  } else if (direction === 1 && progress > 0 && progress <= SCROLL_TOLERANCE) {
    //
    // Weak scroll up
    // Adjust to the bottom of the current section scroll
    // Use stable viewportHeight instead of window.innerHeight for mobile compatibility
    lenis.scrollTo(currentSection.snap + currentSection.height - viewportHeight)
  } else if (direction === 1 && progress > SCROLL_TOLERANCE) {
    assert(nextSection, "Next section not defined")
    //
    // Strong scroll down
    // Scroll to the start of the next section
    lenis.scrollTo(nextSection.snap)
  }
}
